import { GridLayerDetailsDTO } from 'business/models/CustomModels';
import { DrawingLayer } from 'business/models/DrawingLayer';
import {
  GridLayerDTO,
  LayerDTO,
  LayerStateDTO,
  LayerTypeDTO,
} from 'business/models/Models';
import Konva from 'konva';
import {
  getNewId,
  GetRelativePointerPosition,
  getSubGridNumber,
  isNullOrUndefined,
} from 'utils/Global';
import cloneDeep from 'lodash/cloneDeep';
import { getLinkedLayerUniqueName } from '../GenerateUtil';

class RectSides {
  Upper: boolean;
  Lower: boolean;
  Left: boolean;
  Right: boolean;
  constructor() {
    this.Upper = false;
    this.Lower = false;
    this.Left = false;
    this.Right = false;
  }
}

export default class ResizeLayerTools {
  private drawingLayer;
  private layers: any[];
  private getWellFromAbsPoint;
  private selectLayer: DrawingLayer;
  private resizingSide: RectSides;
  private bufferZone: number;
  newLayerRect: any;
  endPos: any;
  layerStartX: number;
  layerStartY: number;
  layerEndY: any;
  layerEndX: any;
  private isMoving: boolean;
  docData: any;
  getPlatePoint: any;
  layerTabRect: any;
  tabNameRect: any;
  privatestartWellX: number;
  selectionColor: string;
  private strokeNormalizer: number;

  constructor(
    drawingLayer,
    docData,
    getWellFromAbsPoint,
    getPlatePoint,
    selectionColor,
    strokeNormalizer: number,
  ) {
    this.drawingLayer = drawingLayer;
    this.getWellFromAbsPoint = getWellFromAbsPoint;
    this.bufferZone = 3;
    this.resizingSide = new RectSides();
    this.docData = cloneDeep(docData);
    this.getPlatePoint = getPlatePoint;
    this.isMoving = false;
    this.selectionColor = selectionColor ? selectionColor : '#FFA500';
    this.strokeNormalizer = strokeNormalizer;
  }

  onMouseDown(layerId = null, selectionOnly = false, preferredLayerIdForResize = null) {
    this.clearCanvas();
    let layerIndex;
    let point;

    if (layerId == null) {
      point = GetRelativePointerPosition(this.drawingLayer);
      layerIndex = this.getMovingLayer(point);
      if (!this.isMoving) {
        layerIndex = this.getResizingLayer(point, preferredLayerIdForResize);
        if (layerIndex >= 0 && this.docData.layers[layerIndex].locked == true) {
          layerIndex = -1;
        }
      }
    } else {
      layerIndex = this.docData.layers.map((layer) => layer.id).indexOf(layerId);
    }

    if (layerIndex >= 0) {
      this.selectLayer = this.docData.layers[layerIndex];
      if (isNullOrUndefined(this.selectLayer.layerRect)) {
        return;
      }
      const layers = [...this.docData.layers];

      this.layerStartX = this.selectLayer.layerRect.x;
      this.layerStartY = this.selectLayer.layerRect.y;
      this.layerEndX = this.selectLayer.layerRect.x + this.selectLayer.layerRect.w;
      this.layerEndY = this.selectLayer.layerRect.y + this.selectLayer.layerRect.h;
      this.tabNameRect = new Konva.Text({
        text: this.selectLayer?.tabNameRect?.props?.text,
        textAnchor: 'middle',
        align: 'center',
        x: this.selectLayer.tabNameRect.props.x,
        y: this.selectLayer.tabNameRect.props.y,
        stroke: '#252525',
        fill: '#252525',
        fontSize: this.selectLayer.tabNameRect.props.fontSize,
        width: this.selectLayer.tabNameRect.props.width,
        height: this.selectLayer.tabNameRect.props.height,
        strokeWidth: 0.1 / this.strokeNormalizer,
        verticalAlign: 'middle',
        wrap: 'none',
        ellipsis: true,
      });

      this.layerTabRect = new Konva.Rect(this.selectLayer.tabRect.props);
      this.layerTabRect.setAttrs({
        opacity: 0.7,
        stroke: this.selectionColor,
        fill: this.selectionColor,
      });

      this.newLayerRect = new Konva.Rect({
        x: this.selectLayer.layerRect.x,
        y: this.selectLayer.layerRect.y,
        stroke: this.selectionColor,
        fill: 'rgba(128,128,128,0.4)',
        strokeWidth: 1.5 / this.strokeNormalizer,
        draggable: false,
        width: this.selectLayer.layerRect.w,
        height: this.selectLayer.layerRect.h,
        listening: false,
      });

      this.drawingLayer?.add(this.newLayerRect);
      this.drawingLayer?.add(this.layerTabRect);
      this.drawingLayer?.add(this.tabNameRect);
      this.drawingLayer?.batchDraw();
      return { layers: layers, selectedLayer: layerIndex };
    }

    return { layers: null, selectedLayer: layerIndex };
  }

  onMouseMove(evt) {
    const wellPositionDetails = evt.target.attrs.wellPositionDetails;
    if (!isNullOrUndefined(this.selectLayer)) {
      if (this.isMoving) {
        // If Moving Layer
        if (wellPositionDetails) {
          this.handleMoving(wellPositionDetails);
        }
      } else {
        // If Resizing Layer
        this.handleResing();
      }

      const tabRectX = this.getTabRect();
      this.layerTabRect.setAttrs({
        x: tabRectX,
        y: this.layerStartY - this.selectLayer.tabRect.props.height,
      });

      this.tabNameRect.setAttrs({
        x: tabRectX,
        y: this.layerStartY - this.selectLayer.tabNameRect.props.height,
      });

      this.drawingLayer.batchDraw();
    }
  }

  private handleMoving(wellPositionDetails: {
    x: number;
    y: number;
    colPos: number;
    rowPos: number;
  }) {
    const width = this.selectLayer.width;
    const height = this.selectLayer.height;
    const point = GetRelativePointerPosition(this.drawingLayer);
    point.y = point.y + this.selectLayer.tabRect.props.height + 2; // To Compensate the height of layer Tab Rectangle, which may fall in upper well
    const startingWell = { x: wellPositionDetails.x, y: wellPositionDetails.y };
    const totalCols = this.docData.colCount * this.docData.containerCols;
    const totalRows = this.docData.rowCount * this.docData.containerRows;
    let isColExceeding = false;
    let isRowExceeding = false;
    const endingWell = { x: startingWell.x + width, y: startingWell.y + height };

    if (totalCols <= endingWell.x) {
      endingWell.x = totalCols - 1;
      isColExceeding = true;
    }
    if (totalRows <= endingWell.y) {
      endingWell.y = totalRows - 1;
      isRowExceeding = true;
    }

    const startingPlateNumber =
      wellPositionDetails.colPos +
      this.docData.colCount * (wellPositionDetails.rowPos - 1);
    const startingPlate = this.docData?.plateDto[startingPlateNumber - 1];
    if (!startingPlate) {
      return;
    }
    let plateWellX = startingWell.x % this.docData.containerCols;
    let plateWellY = startingWell.y % this.docData.containerRows;
    const stWellRect =
      startingPlate.wellRects[plateWellX + plateWellY * this.docData.containerCols];

    const endPlateX =
      startingPlate.colPos -
      1 +
      (Math.floor(endingWell.x / this.docData.containerCols) -
        Math.floor(startingWell.x / this.docData.containerCols));
    const endPlateY =
      startingPlate.rowPos -
      1 +
      (Math.floor(endingWell.y / this.docData.containerRows) -
        Math.floor(startingWell.y / this.docData.containerRows));
    const endingPlate =
      this.docData.plateDto[endPlateX + this.docData.colCount * endPlateY];
    if (!endingPlate) {
      return;
    }
    plateWellX = endingWell.x % this.docData.containerCols;
    plateWellY = endingWell.y % this.docData.containerRows;
    const endWellRect =
      endingPlate.wellRects[plateWellX + plateWellY * this.docData.containerCols];

    if (!isColExceeding) {
      this.layerStartX = stWellRect.x;
      this.layerEndX = endWellRect.x + endWellRect.w;
    }
    if (!isRowExceeding) {
      this.layerStartY = stWellRect.y;
      this.layerEndY = endWellRect.y + endWellRect.h;
    }

    this.newLayerRect.setAttrs({
      x: this.layerStartX,
      y: this.layerStartY,
      width: Math.abs(this.layerStartX - this.layerEndX),
      height: Math.abs(this.layerStartY - this.layerEndY),
    });
  }

  onMouseUp(isNewLinked?: any): {
    layers: LayerDTO[];
    generateRqd: boolean;
    selectedId: number;
  } {
    const strokeBuffer = 2;
    const startWell = this.getWellFromAbsPoint(
      {
        x: this.layerStartX + strokeBuffer,
        y: this.layerStartY + strokeBuffer,
      },
      true,
    );
    const endWell = this.getWellFromAbsPoint({ x: this.layerEndX, y: this.layerEndY });
    const newLayerX = Math.min(startWell.x, endWell.x);
    const newLayerY = Math.min(startWell.y, endWell.y);
    let newLayerWidth = this.selectLayer.width;
    let newLayerHeight = this.selectLayer.height;
    let generateRequired = false;

    if (!this.isMoving) {
      newLayerWidth = Math.abs(endWell.x - startWell.x);
      newLayerHeight = Math.abs(endWell.y - startWell.y);
    }

    this.docData.layers = this.docData.layers.map((lyr) =>
      dirtifyOverLappingLayers(lyr, this.selectLayer),
    ); // Mark overlapping layers before moving as dirty
    let isValidMove = true;
    const tempLayers: LayerDTO[] = cloneDeep(this.docData.layers);

    if (!isNewLinked && !this.isMoving && this.selectLayer.type == LayerTypeDTO.Grid) {
      // Resize all Linked Layers in case of Resizing a Linked layer
      for (let i = 0; i < tempLayers.length; i++) {
        if (
          tempLayers[i].type == LayerTypeDTO.Grid &&
          (tempLayers[i].layerDetail as GridLayerDTO).gridId ==
            (this.selectLayer.layerDetail as GridLayerDTO).gridId
        ) {
          if (
            this.validateLinkedLayer(
              tempLayers[i],
              newLayerX,
              newLayerY,
              newLayerWidth,
              newLayerHeight,
            )
          ) {
            tempLayers[i].x += newLayerX - this.selectLayer.x;
            tempLayers[i].y += newLayerY - this.selectLayer.y;
            tempLayers[i].width += newLayerWidth - this.selectLayer.width;
            tempLayers[i].height += newLayerHeight - this.selectLayer.height;
            tempLayers[i].dirty = true;
          } else {
            isValidMove = false;
            this.resetSelectionRects();
            break;
          }
        }
      }
    }

    if (
      isValidMove &&
      !this.isLayerNotChanged(newLayerX, newLayerY, newLayerHeight, newLayerWidth)
    ) {
      this.selectLayer.x = newLayerX;
      this.selectLayer.y = newLayerY;
      this.selectLayer.height = newLayerHeight;
      this.selectLayer.width = newLayerWidth;
      this.selectLayer.dirty = true;
      this.docData.layers = [...tempLayers];
      generateRequired = true;
    }

    if (isNewLinked) {
      this.addLinkedLayer();
    } else {
      this.docData.layers = this.docData.layers.filter(
        (lyr) => lyr.id != this.selectLayer.id,
      );
      this.docData.layers.unshift(this.selectLayer);
    }
    this.docData.layers = this.docData.layers.map((lyr) =>
      dirtifyOverLappingLayers(lyr, this.selectLayer, true),
    );
    if (this.drawingLayer.children > 0) {
      this.drawingLayer.children[0].setAttrs({ stroke: this.selectionColor });
      this.drawingLayer.children[1].setAttrs({
        stroke: this.selectionColor,
        fill: this.selectionColor,
      });
      this.drawingLayer.children[2].setAttrs({ fill: 'black' });
      this.drawingLayer.batchDraw();
    }
    return {
      layers: this.docData.layers,
      generateRqd: generateRequired,
      selectedId: this.selectLayer.id,
    };
  }

  public getResizingLayer(point, preferredLayerId?: number) {
    let resizeLayer = -1;
    if (!this.docData.layers) {
      return resizeLayer;
    }

    this.resizingSide = new RectSides();

    // When two layers share an edge, prefer the currently selected layer so
    // the user can resize the layer they selected instead of switching selection.
    if (preferredLayerId != null) {
      const preferredIndex = this.docData.layers.findIndex(
        (layer) => layer.id === preferredLayerId,
      );
      if (preferredIndex >= 0) {
        const layer = this.docData.layers[preferredIndex];
        if (
          !isNullOrUndefined(layer.layerRect) &&
          this.isMouseNearBorder(
            point,
            { x: layer.layerRect.x, y: layer.layerRect.y },
            layer.layerRect.h,
            layer.layerRect.w,
          )
        ) {
          return preferredIndex;
        }
        this.resizingSide = new RectSides();
      }
    }

    for (let i = 0; i < this.docData.layers.length; i++) {
      const layer = this.docData.layers[i];
      if (isNullOrUndefined(layer.layerRect)) {
        return;
      }

      if (
        this.isMouseNearBorder(
          point,
          { x: layer.layerRect.x, y: layer.layerRect.y },
          layer.layerRect.h,
          layer.layerRect.w,
        )
      ) {
        resizeLayer = i;
        break;
      }
    }
    return resizeLayer;
  }

  public getMovingLayer(point, isMoving = true) {
    let resizeLayer = -1;
    if (!this.docData.layers) {
      return resizeLayer;
    }

    for (let i = 0; i < this.docData.layers.length; i++) {
      const layer = this.docData.layers[i];
      if (isNullOrUndefined(layer.tabRect)) {
        return;
      }

      if (
        this.isMouseOnRectangle(
          point,
          { x: layer.tabRect.props.x, y: layer.tabRect.props.y },
          layer.tabRect.props.height,
          layer.tabRect.props.width,
        )
      ) {
        resizeLayer = i;
        this.isMoving = isMoving;
        break;
      }
    }
    return resizeLayer;
  }

  private isMouseNearBorder(mousePoint, rectPoint, height, width) {
    if (
      mousePoint.x <= rectPoint.x + this.bufferZone &&
      mousePoint.x >= rectPoint.x - this.bufferZone
    ) {
      if (
        mousePoint.y >= rectPoint.y - this.bufferZone &&
        mousePoint.y <= rectPoint.y + height + this.bufferZone
      ) {
        this.resizingSide.Left = true;
      }
    }
    if (
      mousePoint.x <= rectPoint.x + width + this.bufferZone &&
      mousePoint.x >= rectPoint.x + width - this.bufferZone
    ) {
      if (
        mousePoint.y >= rectPoint.y - this.bufferZone &&
        mousePoint.y <= rectPoint.y + height + this.bufferZone
      ) {
        this.resizingSide.Right = true;
      }
    }
    if (
      mousePoint.y <= rectPoint.y + this.bufferZone &&
      mousePoint.y >= rectPoint.y - this.bufferZone
    ) {
      if (
        mousePoint.x >= rectPoint.x - this.bufferZone &&
        mousePoint.x <= rectPoint.x + width + this.bufferZone
      ) {
        this.resizingSide.Upper = true;
      }
    }
    if (
      mousePoint.y <= rectPoint.y + height + this.bufferZone &&
      mousePoint.y >= rectPoint.y + height - this.bufferZone
    ) {
      if (
        mousePoint.x >= rectPoint.x - this.bufferZone &&
        mousePoint.x <= rectPoint.x + width + this.bufferZone
      ) {
        this.resizingSide.Lower = true;
      }
    }

    return (
      this.resizingSide.Left ||
      this.resizingSide.Right ||
      this.resizingSide.Lower ||
      this.resizingSide.Upper
    );
  }

  private getWellRectangle() {
    const point = GetRelativePointerPosition(this.drawingLayer);
    const endWell = this.getWellFromAbsPoint(point);
    const platePoint = this.getPlatePoint(point);

    const plateWellX = endWell.x % this.docData.containerCols;
    const plateWellY = endWell.y % this.docData.containerRows;
    const endWellRect =
      platePoint.plate.wellRects[plateWellX + plateWellY * this.docData.containerCols];

    return endWellRect;
  }

  private handleResing() {
    const endWellRect = this.getWellRectangle();
    if (isNullOrUndefined(endWellRect)) {
      return;
    }

    const endWellWidth =
      this.selectLayer.x < endWellRect.x ? endWellRect.w : this.selectLayer.width;
    const endWellHeight =
      this.selectLayer.y < endWellRect.y ? endWellRect.h : this.selectLayer.height;

    if (this.resizingSide.Right && endWellRect.x >= this.layerStartX) {
      this.layerEndX = endWellRect.x + endWellWidth;
    }
    if (this.resizingSide.Left && endWellRect.x < this.layerEndX) {
      this.layerStartX = endWellRect.x;
    }

    if (this.resizingSide.Lower && endWellRect.y >= this.layerStartY) {
      this.layerEndY = endWellRect.y + endWellHeight;
    }
    if (this.resizingSide.Upper && endWellRect.y < this.layerEndY) {
      this.layerStartY = endWellRect.y;
    }

    this.newLayerRect.setAttrs({
      x: this.layerStartX,
      y: this.layerStartY,
      width: Math.abs(this.layerStartX - this.layerEndX),
      height: Math.abs(this.layerStartY - this.layerEndY),
    });
  }

  public getCursorClass(preferredLayerId?: number) {
    const point = GetRelativePointerPosition(this.drawingLayer);
    let layerIndex = this.getMovingLayer(point);

    if (layerIndex >= 0) {
      return 'cursor-pointer';
    }

    layerIndex = this.getResizingLayer(point, preferredLayerId);

    if (layerIndex >= 0 || !isNullOrUndefined(this.selectLayer)) {
      if (
        (this.resizingSide.Left && this.resizingSide.Lower) ||
        (this.resizingSide.Upper && this.resizingSide.Right)
      ) {
        return 'resize-pointer-ne';
      } else if (
        (this.resizingSide.Left && this.resizingSide.Upper) ||
        (this.resizingSide.Lower && this.resizingSide.Right)
      ) {
        return 'resize-pointer-nw';
      } else if (this.resizingSide.Left || this.resizingSide.Right) {
        return 'resize-pointer-hor';
      } else if (this.resizingSide.Upper || this.resizingSide.Lower) {
        return 'resize-pointer-ver';
      }
    }
    return '';
  }

  private isMouseOnRectangle(mousePoint, rectPoint, height, width) {
    if (
      mousePoint.x >= rectPoint.x + this.bufferZone &&
      mousePoint.x <= rectPoint.x + width + this.bufferZone
    ) {
      if (
        mousePoint.y + this.bufferZone >= rectPoint.y &&
        mousePoint.y <= rectPoint.y + height + this.bufferZone
      ) {
        return true;
      }
    }
    return false;
  }

  clearCanvas() {
    if (this.drawingLayer) {
      this.drawingLayer.destroyChildren();
      this.drawingLayer.clear();
      this.drawingLayer.batchDraw();
    }
  }

  getTabRect() {
    let tabRectX = this.layerStartX;
    let overlappingLayer = 0;
    let count = 0;
    while (overlappingLayer > -1 && count < this.docData.layers.length) {
      overlappingLayer = this.getMovingLayer(
        {
          x: tabRectX + 5,
          y: this.layerStartY - this.selectLayer.tabRect.props.height * 0.75,
        },
        this.isMoving,
      ); // to avoid overlappping of TabRects
      if (
        overlappingLayer >= 0 &&
        this.docData.layers[overlappingLayer].id != this.selectLayer.id
      ) {
        tabRectX = this.docData.layers[overlappingLayer].tabRect.props.x + 10;
      }
      count++;
    }
    return tabRectX;
  }

  drawLinkedLayer(layer) {
    this.clearCanvas();

    this.selectLayer = layer;
    this.layerStartX = this.selectLayer.layerRect.x;
    this.layerStartY = this.selectLayer.layerRect.y;
    this.layerEndX = this.selectLayer.layerRect.x + this.selectLayer.layerRect.w;
    this.layerEndY = this.selectLayer.layerRect.y + this.selectLayer.layerRect.h;
    this.isMoving = true;

    this.tabNameRect = new Konva.Text({
      text: this.selectLayer.tabNameRect.props.text,
      textAnchor: 'middle',
      align: 'center',
      x: this.selectLayer.tabNameRect.props.x,
      y: this.selectLayer.tabNameRect.props.y,
      stroke: '#252525',
      fontSize: this.selectLayer.tabNameRect.props.fontSize,
      width: this.selectLayer.tabNameRect.props.width,
      height: this.selectLayer.tabNameRect.props.height,
      strokeWidth: 0.1 / this.strokeNormalizer,
      verticalAlign: 'middle',
      wrap: 'none',
      ellipsis: true,
    });

    this.layerTabRect = new Konva.Rect(this.selectLayer.tabRect.props);
    this.layerTabRect.setAttrs({
      stroke: this.selectionColor,
      fill: this.selectionColor,
    });

    this.newLayerRect = new Konva.Rect({
      x: this.selectLayer.layerRect.x,
      y: this.selectLayer.layerRect.y,
      stroke: this.selectionColor,
      fill: 'rgba(128,128,128,0.4)',
      strokeWidth: 1.5 / this.strokeNormalizer,
      draggable: false,
      width: this.selectLayer.layerRect.w,
      height: this.selectLayer.layerRect.h,
    });

    this.drawingLayer.add(this.newLayerRect);
    this.drawingLayer.add(this.layerTabRect);
    this.drawingLayer.add(this.tabNameRect);
    this.drawingLayer.batchDraw();
  }

  private validateLinkedLayer(
    layer,
    newLayerX,
    newLayerY,
    newLayerWidth,
    newLayerHeight,
  ) {
    const newX = layer.x + (newLayerX - this.selectLayer.x);
    const newY = layer.y + (newLayerY - this.selectLayer.y);
    const endX = newX + (layer.width + (newLayerWidth - this.selectLayer.width));
    const endY = newY + (layer.height + (newLayerHeight - this.selectLayer.height));
    const totalCols = this.docData.containerCols * this.docData.colCount;
    const totalRows = this.docData.containerRows * this.docData.rowCount;

    if (
      newX >= totalCols ||
      newX < 0 ||
      newY >= totalRows ||
      newY < 0 ||
      endX >= totalCols ||
      endX < 0 ||
      endY >= totalRows ||
      endY < 0
    ) {
      return false;
    }
    return true;
  }

  private resetSelectionRects() {
    this.tabNameRect.setAttrs({
      text: this.selectLayer.tabNameRect.props.text,
      textAnchor: 'middle',
      align: 'center',
      x: this.selectLayer.tabNameRect.props.x,
      y: this.selectLayer.tabNameRect.props.y,
      stroke: '#252525',
      fontSize: this.selectLayer.tabNameRect.props.fontSize,
      width: this.selectLayer.tabNameRect.props.width,
      height: this.selectLayer.tabNameRect.props.height,
      strokeWidth: 0.1 / this.strokeNormalizer,
      verticalAlign: 'middle',
      wrap: 'none',
      ellipsis: true,
    });

    this.layerTabRect.setAttrs(this.selectLayer.tabRect.props);
    this.layerTabRect.setAttrs({
      stroke: this.selectionColor,
      fill: this.selectionColor,
    });

    this.newLayerRect.setAttrs({
      x: this.selectLayer.layerRect.x,
      y: this.selectLayer.layerRect.y,
      stroke: this.selectionColor,
      fill: 'rgba(128,128,128,0.4)',
      strokeWidth: 1.5 / this.strokeNormalizer,
      draggable: false,
      width: this.selectLayer.layerRect.w,
      height: this.selectLayer.layerRect.h,
    });
  }

  private addLinkedLayer() {
    const newlayer = new LayerDTO();
    newlayer.id = getNewId();
    newlayer.x = this.selectLayer.x;
    newlayer.y = this.selectLayer.y;
    newlayer.width = this.selectLayer.width;
    newlayer.height = this.selectLayer.height;
    newlayer.type = this.selectLayer.type;
    newlayer.volume = this.docData.defWellVolume;
    newlayer.targetDrop = -1;
    newlayer.overrideLayer = false;
    newlayer.locked = false;
    newlayer.expId = this.selectLayer.expId;
    newlayer.layerState = LayerStateDTO.New;
    newlayer.volumeBasedAddition = false;
    newlayer.dirty = true;

    const layerDetail = new GridLayerDTO();
    layerDetail.gridId = (this.selectLayer.layerDetail as GridLayerDTO).gridId;
    layerDetail.id = getNewId();
    layerDetail.layerId = newlayer.id;
    layerDetail.subGridNumber = getSubGridNumber(this.docData, this.selectLayer);

    newlayer.layerDetail = layerDetail;

    newlayer.name = getLinkedLayerUniqueName(this.docData.layers, newlayer);

    this.docData.layers.unshift(newlayer);
  }

  private isLayerNotChanged(x, y, height, width) {
    if (
      x == this.selectLayer.x &&
      y == this.selectLayer.y &&
      width == this.selectLayer.width &&
      height == this.selectLayer.height
    ) {
      return true;
    }
    return false;
  }
}

export function dirtifyOverLappingLayers(
  layer: LayerDTO,
  operandLayer: LayerDTO,
  includeLinkedLayers = false,
): LayerDTO {
  if (
    operandLayer.x > layer.x + layer.width ||
    operandLayer.x + operandLayer.width < layer.x ||
    operandLayer.y > layer.y + layer.height ||
    operandLayer.y + operandLayer.height < layer.y
  ) {
    if (
      includeLinkedLayers &&
      operandLayer.type == LayerTypeDTO.Grid &&
      layer.type == LayerTypeDTO.Grid &&
      (operandLayer.layerDetail as GridLayerDetailsDTO).gridId ==
        (layer.layerDetail as GridLayerDetailsDTO).gridId
    ) {
      layer.dirty = true;
    }
    return layer;
  }

  layer.dirty = true;
  return layer;
}
